"use client";

import React from "react";
import { cn } from "@/app/_module/lib/utils";
import type { RegistrationType } from "./Step0RegistrationTypeSelect";

interface WizardProgressProps {
  currentStep: number;
  completedSteps: number[];
  registrationType?: RegistrationType | null;
  /** Right side of the "Step X of Y" line, e.g. "Finish later". */
  action?: React.ReactNode;
}

export default function WizardProgress({
  currentStep,
  completedSteps,
  registrationType,
  action,
}: WizardProgressProps) {
  // Sole Proprietor skips EIN (1) and CP 575 (2): 0 -> 3 -> 4.
  // Low Volume Standard runs the full flow: 0 -> 1 -> 2 -> 3 -> 4.
  const steps =
    registrationType === "sole_proprietor"
      ? [
          { number: 0, label: "Registration type" },
          { number: 3, label: "Your details" },
          { number: 4, label: "Messages" },
        ]
      : [
          { number: 0, label: "Registration type" },
          { number: 1, label: "EIN" },
          { number: 2, label: "CP 575 letter" },
          { number: 3, label: "Business details" },
          { number: 4, label: "Messages" },
        ];

  const index = Math.max(0, steps.findIndex((s) => s.number === currentStep));

  return (
    <div className="space-y-2.5">
      <div className="flex min-h-9 items-center justify-between gap-3">
        <p className="text-sm text-neutral-500">
          <span className="font-medium tabular-nums text-neutral-900">
            Step {index + 1} of {steps.length}
          </span>{" "}
          · {steps[index]?.label}
        </p>
        {action}
      </div>
      <ol aria-label="Registration steps" className="flex gap-1.5">
        {steps.map((step) => {
          const current = step.number === currentStep;
          const done = !current && completedSteps.includes(step.number);
          return (
            <li key={step.number} className="flex-1">
              <span
                aria-hidden
                className={cn("block h-1 rounded-full transition-colors", done || current ? "bg-neutral-900" : "bg-neutral-200")}
              />
              <span className="sr-only">
                {step.label}: {current ? "current step" : done ? "done" : "not started"}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
